import { useCallback } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import StatusPill from "../../../pages/cashouts/CashoutCard/StatusPill";
import Spinner from "../Spinner";
import { TableItem } from "./DetailsPageLayout";
import styles from "./DetailsPageLayout.module.scss";

export interface TransactionTableProps {
  transactionsSectionTitle: string;
  showAllLinkText?: string;
  onShowAllClick?: () => void;
  pageLoading: boolean;
  items: TableItem[];
  tableHeaders: {
    transaction?: string;
    transactionNumber?: string;
    totalValue?: string;
    status?: string;
  };
  renderTableRow?: (item: TableItem, index: number) => React.ReactNode;
  getItemStatus?: (item: TableItem, overallStatus?: string) => string;
  status?: string;
  onContractClick?: (contractId: string) => void;
  onCopy?: (text: string) => void;
  currencyLabel?: string;
}

const TransactionTable = ({
  transactionsSectionTitle,
  showAllLinkText,
  onShowAllClick,
  pageLoading,
  items,
  tableHeaders,
  renderTableRow,
  getItemStatus,
  status,
  onContractClick,
  onCopy,
  currencyLabel,
}: TransactionTableProps) => {
  const { t } = useTranslation();

  const handleCopy = useCallback(
    async (text: string) => {
      if (onCopy) {
        await onCopy(text);
      } else {
        try {
          await navigator?.clipboard?.writeText(text);
        } catch (error) {
          console.error("Error copying to clipboard:", error);
        }
      }
    },
    [onCopy]
  );

  const renderDefaultTableRow = useCallback(
    (item: TableItem, index: number) => {
      const itemStatus = getItemStatus
        ? getItemStatus(item, status)
        : status || "";

      return (
        <tr key={index} className={styles.table_row}>
          <td
            className={styles.table_cell}
            onClick={() =>
              item.contractId && onContractClick?.(item.contractId)
            }>
            {item.title}
          </td>
          <td
            style={{ display: "flex", gap: "8px" }}
            className={classNames(styles.table_cell, styles.inline_cell)}>
            <p>{item.contractId || item.id}</p>
            <img
              src="/assets/images/svg/copy.svg"
              alt="copy"
              onClick={() => handleCopy(item.contractId || item.id)}
              className={styles.copy_icon}
            />
          </td>
          <td className={styles.table_cell}>
            {item.amount.toLocaleString()}{" "}
            {currencyLabel || t("lbl_saudi_currency")}
          </td>
          <td className={classNames(styles.table_cell, styles.inline_cell)}>
            <StatusPill status={itemStatus} />
            {item.contractId && (
              <img
                src="/assets/images/svg/arrow2.svg"
                alt="تفاصيل"
                onClick={() => {
                  if (item.contractId) {
                    onContractClick?.(item.contractId);
                  }
                }}
                style={{ cursor: "pointer" }}
                className={styles.arrow_icon}
              />
            )}
          </td>
        </tr>
      );
    },
    [getItemStatus, status, onContractClick, handleCopy, currencyLabel, t]
  );

  const renderTable = useCallback(
    (itemsToRender: TableItem[]) => {
      return (
        <div className={styles.table_container}>
          <table style={{ width: "100%" }} className={styles.contracts_table}>
            <thead>
              <tr
                style={{ background: "#f7f8fb", height: "56px" }}
                className={styles.table_header}>
                {Object.values(tableHeaders).map((header) => (
                  <th style={{ textAlign: "right" }} key={header}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={styles.table_body}>
              {itemsToRender.map((item, index) =>
                renderTableRow
                  ? renderTableRow(item, index)
                  : renderDefaultTableRow(item, index)
              )}
            </tbody>
          </table>
        </div>
      );
    },
    [tableHeaders, renderTableRow, renderDefaultTableRow]
  );

  return (
    <div className={classNames(styles.contracts_section, styles.card)}>
      <div className={styles.contracts_header}>
        <h3 className={styles.section_title}>{transactionsSectionTitle}</h3>
        {showAllLinkText && (
          <p
            onClick={() => {
              if (items.length > 0 && onShowAllClick) {
                onShowAllClick();
              }
            }}
            className={classNames(
              styles["link_Text"],
              items.length === 0 && styles["link_disabled"]
            )}>
            {showAllLinkText}
          </p>
        )}
      </div>
      {pageLoading ? <Spinner /> : renderTable(items || [])}
    </div>
  );
};

export default TransactionTable;
